import axios from 'axios';
import { getCmsApiBaseUrl } from './api.config';

export interface GetClientsParams {
  page?: number;
  per_page?: number;
}

export interface Client {
  _id: string;
  name: string;
  authorgid?: string | null;
}

export interface GetClientsResponse {
  clients: Client[];
  page: number;
  per_page: number;
  total: number;
}

/**
 * Get all clients with pagination
 */
export const getAllClients = async (
  params: GetClientsParams = {},
  token: string,
  liveData?: boolean,
): Promise<GetClientsResponse> => {
  const { page = 0, per_page = 100 } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: per_page.toString(),
  });

  const response = await axios.get(
    `${getCmsApiBaseUrl(liveData)}/clients?${queryParams}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  return response.data;
};

function pickApiKey(payload: unknown): string {
  if (typeof payload === 'string' && payload.trim()) return payload.trim();
  if (!payload || typeof payload !== 'object') return '';
  const data = payload as Record<string, unknown>;
  const nested =
    data.data && typeof data.data === 'object'
      ? (data.data as Record<string, unknown>)
      : data;
  const candidates = [
    nested.apiKey,
    nested.api_key,
    nested.key,
    nested.secret,
    nested.secretKey,
    data.apiKey,
    data.api_key,
    data.key,
  ];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

/**
 * Reveal a client's API key. Only called when the user explicitly requests it.
 */
export const getClientApiKey = async (
  customerId: string,
  token: string,
  liveData?: boolean,
): Promise<string> => {
  const response = await axios.get(
    `${getCmsApiBaseUrl(liveData)}/clients/${encodeURIComponent(
      customerId,
    )}/api-key`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  const apiKey = pickApiKey(response.data);
  if (!apiKey) {
    throw new Error('API key was not returned');
  }
  return apiKey;
};

export type ClientWebhookSettings = {
  url: string;
  username: string;
  password: string;
  retryOn500: boolean;
};

function unwrapRecord(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return {};
  const data = payload as Record<string, unknown>;
  if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
    return data.data as Record<string, unknown>;
  }
  if (
    data.webhook &&
    typeof data.webhook === 'object' &&
    !Array.isArray(data.webhook)
  ) {
    return data.webhook as Record<string, unknown>;
  }
  return data;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === 1 || value === '1') return true;
  if (value === 'false' || value === 0 || value === '0') return false;
  return fallback;
}

export function parseClientWebhook(
  payload: unknown,
): ClientWebhookSettings {
  const data = unwrapRecord(payload);
  return {
    url: asString(data.url || data.webhookUrl || data.targetUrl),
    username: asString(data.username || data.user),
    password: asString(data.password),
    retryOn500: asBoolean(data.retryOn500 ?? data.retries, true),
  };
}

function clientWebhookUrl(customerId: string, liveData?: boolean): string {
  return `${getCmsApiBaseUrl(liveData)}/clients/${encodeURIComponent(
    customerId,
  )}/webhook`;
}

export const getClientWebhook = async (
  customerId: string,
  token: string,
  liveData?: boolean,
): Promise<ClientWebhookSettings> => {
  try {
    const response = await axios.get(clientWebhookUrl(customerId, liveData), {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return parseClientWebhook(response.data);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return { url: '', username: '', password: '', retryOn500: true };
    }
    throw error;
  }
};

export const updateClientWebhook = async (
  customerId: string,
  settings: ClientWebhookSettings,
  token: string,
  liveData?: boolean,
): Promise<ClientWebhookSettings> => {
  const body = {
    url: settings.url,
    username: settings.username,
    password: settings.password,
    retryOn500: settings.retryOn500,
  };
  const response = await axios.put(
    clientWebhookUrl(customerId, liveData),
    body,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  const parsed = parseClientWebhook(response.data ?? body);
  if (parsed.url || parsed.username || parsed.password) return parsed;
  return body;
};

function clientUrl(customerId: string, path: string, liveData?: boolean): string {
  return `${getCmsApiBaseUrl(liveData)}/clients/${encodeURIComponent(
    customerId,
  )}${path}`;
}

function asHours(value: unknown, fallback = 72): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(n) || n < 1 || n > 720) return fallback;
  return n;
}

export function parseSessionExpiry(payload: unknown): number {
  const data = unwrapRecord(payload);
  return asHours(
    data.sessionExpireAfter ??
      data.sessionexpireafter ??
      data.sessionExpiry ??
      data.hours,
    72,
  );
}

export const getClientSessionExpiry = async (
  customerId: string,
  token: string,
  liveData?: boolean,
): Promise<number> => {
  try {
    const response = await axios.get(
      clientUrl(customerId, '/session-expiry', liveData),
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );
    return parseSessionExpiry(response.data);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return 72;
    }
    throw error;
  }
};

export const updateClientSessionExpiry = async (
  customerId: string,
  sessionExpireAfter: number,
  token: string,
  liveData?: boolean,
): Promise<number> => {
  const body = { sessionExpireAfter };
  const response = await axios.put(
    clientUrl(customerId, '/session-expiry', liveData),
    body,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  if (response.data == null || response.data === '') {
    return sessionExpireAfter;
  }
  return parseSessionExpiry(response.data);
};

export const previewClientWebhook = async (
  customerId: string,
  token: string,
  liveData?: boolean,
): Promise<unknown> => {
  const response = await axios.post(
    clientUrl(customerId, '/webhook/test', liveData),
    undefined,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data;
};

export type SendClientWebhookBody = {
  url: string;
  username?: string;
  password?: string;
};

/**
 * Deliver a test webhook to the client's target URL.
 * POST /clients/{customerId}/webhook/test/send
 */
export const sendClientWebhook = async (
  customerId: string,
  body: SendClientWebhookBody,
  token: string,
  liveData?: boolean,
): Promise<unknown> => {
  const response = await axios.post(
    clientUrl(customerId, '/webhook/test/send', liveData),
    {
      url: body.url,
      username: body.username ?? '',
      password: body.password ?? '',
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data;
};
